import React, { useState } from 'react';
import './CleanerChecklist.css';

interface ChecklistItem {
  id: string;
  text: string;
  checked: boolean;
}

interface RoomChecklist {
  id: string;
  name: string;
  items: ChecklistItem[];
}

const isSpanish = navigator.language.startsWith('es');

const t = {
  title: isSpanish ? 'Inspección de la propiedad Gonzaga' : 'Gonzaga property inspection',
  overallProgress: isSpanish ? 'Progreso general' : 'Overall Progress',
  itemsCompleted: isSpanish
    ? (checked: number, total: number) => `${checked} de ${total} elementos completados`
    : (checked: number, total: number) => `${checked} of ${total} items completed`,
  previousRoom: isSpanish ? '← Habitación anterior' : '← Previous Room',
  nextRoom: isSpanish ? 'Siguiente habitación →' : 'Next Room →',
};

const roomData = {
  kitchen: {
    name: isSpanish ? 'Cocina' : 'Kitchen',
    items: isSpanish ? [
      'Buscar platos en cada habitación y el fregadero, ponerlos en el lavavajillas y ejecutar el ciclo rápido',
      'Revisar el refrigerador en busca de comida sobrante, tirarla a la basura',
      'Revisar cada gabinete y estante, asegurarse de que no haya artículos fuera de lugar o desorden notable',
      'Revisar el microondas, asegurarse de que esté limpio por dentro',
      'Revisar el horno y la estufa, asegurarse de que estén limpios por dentro',
      'Revisar la estación de café, asegurarse de que esté limpia por dentro',
      'Asegurarse de que la estación de café esté bien surtida con crema, cápsulas, azúcar y granos de café',
      'Vaciar el bote de basura (está dentro del gabinete debajo del fregadero)',
      'Asegurarse de que haya toallas de papel en la cocina',
      'Verificar que haya toallas de papel de repuesto debajo del fregadero',
      'Verificar que haya una esponja disponible en el fregadero (limpia, sin usar)',
      'Verificar que haya bolsas de basura de repuesto debajo del fregadero',
      'Verificar que haya cápsulas de lavavajillas debajo del fregadero',
      'Verificar que el jabón del lavavajillas esté lleno en la encimera',
      'Dejar chocolate para los huéspedes',
    ] : [
      'Check for dishes in every room and sink, put in dishwasher and run quick setting load',
      'Check fridge for leftover food, put in trash',
      'Check every cabinet and shelf, make sure theres no misplaced items or noticeable mess',
      'Check microwave, make sure clean inside',
      'Check oven and stovetop, make sure clean inside',
      'Check coffee station, make sure clean inside',
      'Make sure coffee station is adequately filled with creamer, pods, sugar, and coffee beans',
      'Empty trash can (trash can is inside the cabinet under the sink)',
      'Ensure paper towels on kitchen are placed',
      'Ensure backup paper towel under sink cabinet',
      'Ensure kitchen sponge available on sink (and clean, not used)',
      'Ensure backup garbage bag under sink',
      'Ensure dishwasher pods are under sink',
      'Ensure dishwasher soap is full on countertop',
      'Leave out chocolate for guests',
    ],
  },
  bedroom: {
    name: (n: number) => isSpanish ? `Habitación ${n}` : `Bedroom ${n}`,
    items: isSpanish ? [
      'Verificar que la cama esté tendida según el estándar y lista para los huéspedes',
      'Asegurarse de que las lámparas y los televisores estén enchufados y que los huéspedes no tengan que mover muebles',
      'Abrir e inspeccionar todos los cajones para verificar artículos olvidados (incluidos artículos prohibidos)',
      'Revisar los closets para asegurarse de que estén limpios, sin pertenencias de huéspedes y bien organizados',

      'Inspeccionar visualmente si hay cabello en la cama u otras superficies visibles',
    ] : [
      'Verify the bed is made to standard and is guest-ready',
      'Ensure bedside lamps and TVs are plugged in and do not require guests to move furniture',
      'Open and inspect all drawers to check for forgotten items (including prohibited items)',
      'Check closets to ensure they are clean, empty of guest belongings, and properly staged',

      'Visually inspect for any hair on the bed or other visible surfaces',
    ],
  },
  bathroom: {
    name: (n: number) => isSpanish ? `Baño ${n}` : `Bathroom ${n}`,
    items: isSpanish ? [
      'Asegurarse de que haya suficiente papel higiénico de repuesto (p. ej., tres rollos debajo del fregadero)',
      'Confirmar que los dispensadores de jabón estén llenos y con las boquillas hacia adelante',
      'Toalla de manos, toalla de baño y toalla de maquillaje presentes',
      'Asegurarse de que el tapete de baño esté en su lugar',
      'Inspeccionar el botiquín de primeros auxilios para confirmar que esté completamente surtido (curitas, antiséptico, etc.)',
    ] : [
      'Ensure adequate extra toilet paper is stocked (e.g., three rolls under the sink)',
      'Confirm soap bottles are full and nozzles are facing forward for presentation',
      'Hand towel, bath towel and makeup towel present',
      'Ensure the bath mat is in place',
      'Inspect the first aid kit to confirm it is fully stocked (Band-Aids, antiseptic, etc.)',
    ],
  },
  laundryroom: {
    name: isSpanish ? 'Lavandería' : 'Laundry Room',
    items: isSpanish ? [
      'Verificar que la lavadora esté vacía',
      'Asegurarse de que haya suficiente detergente',
    ] : [
      'Check that laundry is empty',
      'There is enough detergent',
    ],
  },
  livingroom: {
    name: isSpanish ? 'Sala de estar' : 'Living Room',
    items: isSpanish ? [
      'Tomar fotos de manchas o desorden importante para reportar a Airbnb',
      'Asegurarse de que el control remoto del televisor esté en la mesa de centro',
      'Asegurarse de que las decoraciones de la mesa de centro estén limpias (sin desorden en la canasta)',
      'Verificar que los posavasos estén colocados en la mesa de centro',
      'Asegurarse de que las almohadas estén organizadas en los sofás y sillas',
      'Asegurarse de que las sillas estén alineadas',
      'Inspeccionar y eliminar cualquier mancha en el sofá o la alfombra',
      'Asegurarse de que el televisor pueda encenderse y esté enchufado',
      'Asegurarse de que las luces LED estén enchufadas en el lugar correcto (una en el pasillo y dos en los enchufes de la sala)',
    ] : [
      'Take pictures of any major stains or mess to report to Airbnb',
      'Make sure TV remote is on coffee table',
      'Make sure coffee table decorations are clean (no random mess on the basket)',
      'Ensure coasters are placed on coffee table',
      'Make sure pillows are staged on sofas and chairs',
      'Make sure chairs are aligned',
      'Inspect and remove any stains on couch or rug',
      'Make sure TV can power on and is plugged in',
      'Make sure LED plug lights are plugged in and in the right place (one in hallway, and two in living room plugs)',
    ],
  },
  final: {
    name: isSpanish ? 'Antes de salir' : 'Before You Leave',
    items: isSpanish ? [
      'Ajustar la temperatura a un nivel adecuado para los huéspedes',
      'Asegurarse de que todas las ventanas estén cerradas con llave',
      'Hacer una revisión final del refrigerador para asegurarse de que no queden artículos personales',
      'Confirmar que toda la ropa (incluyendo el contenido de la aspiradora y la secadora) esté fuera de la vista',
      'Asegurarse de que la iluminación exterior esté configurada para guiar a los huéspedes, especialmente en los meses más oscuros',
      'Verificar que las llaves y el control del garaje estén guardados en el cajón más a la izquierda de la cocina',
    ] : [
      'Set the temperature to a guest-ready level',
      'Ensure all windows are locked',
      'Do a final refrigerator check to ensure no personal items remain',
      'Confirm all laundry (including vacuum and dryer contents) is out of sight',
      'Ensure exterior lighting is set to guide guests, especially during darker months',
      'Verify keys and garage remote are stored in the leftmost kitchen drawer',
    ],
  },
};

const buildRooms = (): RoomChecklist[] => [
  {
    id: 'kitchen',
    name: roomData.kitchen.name,
    items: roomData.kitchen.items.map((text, i) => ({ id: `k${i}`, text, checked: false })),
  },
  {
    id: 'bedroom1',
    name: roomData.bedroom.name(1),
    items: roomData.bedroom.items.map((text, i) => ({ id: `b1_${i + 1}`, text, checked: false })),
  },
  {
    id: 'bedroom2',
    name: roomData.bedroom.name(2),
    items: roomData.bedroom.items.map((text, i) => ({ id: `b2_${i + 1}`, text, checked: false })),
  },
  {
    id: 'bedroom3',
    name: roomData.bedroom.name(3),
    items: roomData.bedroom.items.map((text, i) => ({ id: `b3_${i + 1}`, text, checked: false })),
  },
  {
    id: 'bathroom1',
    name: roomData.bathroom.name(1),
    items: roomData.bathroom.items.map((text, i) => ({ id: `ba1_${i + 1}`, text, checked: false })),
  },
  {
    id: 'bathroom2',
    name: roomData.bathroom.name(2),
    items: roomData.bathroom.items.map((text, i) => ({ id: `ba2_${i + 1}`, text, checked: false })),
  },
  {
    id: 'laundryroom',
    name: roomData.laundryroom.name,
    items: roomData.laundryroom.items.map((text, i) => ({ id: `lr${i + 1}`, text, checked: false })),
  },
  {
    id: 'livingroom',
    name: roomData.livingroom.name,
    items: roomData.livingroom.items.map((text, i) => ({ id: `l${i}`, text, checked: false })),
  },
  {
    id: 'final',
    name: roomData.final.name,
    items: roomData.final.items.map((text, i) => ({ id: `f${i + 1}`, text, checked: false })),
  },
];

const CleanerChecklist: React.FC = () => {
  const initialRooms = buildRooms();

  const [rooms, setRooms] = useState<RoomChecklist[]>(initialRooms);

  const [currentRoomIndex, setCurrentRoomIndex] = useState(0);

  const toggleItem = (roomId: string, itemId: string) => {
    setRooms(prevRooms =>
      prevRooms.map(room =>
        room.id === roomId
          ? {
              ...room,
              items: room.items.map(item =>
                item.id === itemId ? { ...item, checked: !item.checked } : item
              ),
            }
          : room
      )
    );
  };

  const getProgress = (room: RoomChecklist) => {
    const checkedCount = room.items.filter(item => item.checked).length;
    return { checked: checkedCount, total: room.items.length };
  };

  const isRoomComplete = (room: RoomChecklist) => {
    return room.items.every(item => item.checked);
  };

  const getTotalProgress = () => {
    const totalItems = rooms.reduce((sum, room) => sum + room.items.length, 0);
    const checkedItems = rooms.reduce(
      (sum, room) => sum + room.items.filter(item => item.checked).length,
      0
    );
    return { checked: checkedItems, total: totalItems };
  };

  const goToNextRoom = () => {
    if (currentRoomIndex < rooms.length - 1) {
      setCurrentRoomIndex(currentRoomIndex + 1);
    }
  };

  const goToPreviousRoom = () => {
    if (currentRoomIndex > 0) {
      setCurrentRoomIndex(currentRoomIndex - 1);
    }
  };

  const currentRoom = rooms[currentRoomIndex];
  const progress = getProgress(currentRoom);
  const totalProgress = getTotalProgress();

  return (
    <div className="cleaner-checklist">
      <header className="checklist-header">
        <h1>{t.title}</h1>
        <div className="overall-progress">
          <span>{t.overallProgress}: {totalProgress.checked}/{totalProgress.total}</span>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${(totalProgress.checked / totalProgress.total) * 100}%` }}
            ></div>
          </div>
        </div>
      </header>

      <div className="room-navigation">
        {rooms.map((room, index) => (
          <button
            key={room.id}
            className={`room-nav-button ${index === currentRoomIndex ? 'active' : ''} ${
              isRoomComplete(room) ? 'complete' : ''
            }`}
            onClick={() => setCurrentRoomIndex(index)}
          >
            <span className="room-name">{room.name}</span>
            <span className="room-progress">
              {getProgress(room).checked}/{getProgress(room).total}
            </span>
            {isRoomComplete(room) && <span className="checkmark">✓</span>}
          </button>
        ))}
      </div>

      <div className="room-content">
        <div className="room-header">
          <h2>{currentRoom.name}</h2>
          <p className="room-subtitle">
            {t.itemsCompleted(progress.checked, progress.total)}
          </p>
        </div>

        <div className="checklist-items">
          {currentRoom.items.map(item => (
            <label key={item.id} className={`checklist-item ${item.checked ? 'checked' : ''}`}>
              <input
                type="checkbox"
                checked={item.checked}
                onChange={() => toggleItem(currentRoom.id, item.id)}
              />
              <span className="checkbox-custom"></span>
              <span className="item-text">{item.text}</span>
            </label>
          ))}
        </div>

        <div className="room-actions">
          <button
            className="nav-button prev"
            onClick={goToPreviousRoom}
            disabled={currentRoomIndex === 0}
          >
            {t.previousRoom}
          </button>
          <button
            className="nav-button next"
            onClick={goToNextRoom}
            disabled={currentRoomIndex === rooms.length - 1}
          >
            {t.nextRoom}
          </button>
        </div>
      </div>

    </div>
  );
};

export default CleanerChecklist;
